import React from 'react';
import { Button } from '@/components/ui/button';
import { Bot, Loader2 } from 'lucide-react';

interface AiAnalysisButtonProps {
  onClick: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  size?: 'sm' | 'default' | 'lg';
  className?: string;
  showText?: boolean;
}

/**
 * AiAnalysisButton - Reusable AI Analysis button with consistent styling and animation
 * Features:
 * - Colorful beam effect around icon (always visible)
 * - Black background with white text
 * - Loading state with animated spinner
 * - Consistent across all tabs
 */
const AiAnalysisButton: React.FC<AiAnalysisButtonProps> = ({
  onClick,
  disabled = false,
  isLoading = false,
  size = 'sm',
  className = '',
  showText = true
}) => {
  return (
    <Button
      onClick={onClick}
      disabled={disabled || isLoading}
      size={size}
      className={`group relative flex items-center gap-2 bg-black hover:bg-gray-900 text-white shadow-lg hover:shadow-xl transition-all duration-300 font-medium transform hover:scale-105 active:scale-95 ${className}`}
    >
      {isLoading ? (
        <div className="relative">
          <Loader2 className="h-4 w-4 animate-spin" />
          <div className="absolute inset-0 rounded-full animate-pulse bg-gradient-to-r from-green-400 via-yellow-400 to-red-400 opacity-30 blur-sm"></div>
        </div>
      ) : (
        <div className="relative">
          <Bot className="h-4 w-4" />
          {/* Beam effect - rotating border around icon only - ALWAYS VISIBLE */}
          <div className="absolute -inset-0.5 rounded-full opacity-100">
            <div className="absolute inset-0 rounded-full" style={{
              background: 'conic-gradient(from 0deg, #10b981, #fbbf24, #ef4444, #10b981)',
              animation: 'spin 2s linear infinite',
              padding: '1px'
            }}>
              <div className="absolute inset-0 rounded-full bg-black"></div>
            </div>
          </div>
        </div>
      )}
      {showText && (
        <span className="relative z-10 transition-all duration-300 group-hover:font-semibold">
          {isLoading ? (
            <span className="flex items-center gap-1">
              <span className="animate-pulse">Analyzing</span>
              <span className="animate-bounce delay-100">.</span>
              <span className="animate-bounce delay-200">.</span>
              <span className="animate-bounce delay-300">.</span>
            </span>
          ) : (
            'AI Analysis'
          )}
        </span>
      )}
    </Button>
  );
};

export default AiAnalysisButton;